import { useState } from 'react';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { UserAvatar } from '@/components/user';
import { Send, Loader2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

export function Composer({
  replyTo,
  onPublished,
  placeholder = "What's happening?",
  autoFocus = false,
}: {
  replyTo?: { id: string; pubkey: string };
  onPublished?: () => void;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  const { user } = useCurrentUser();
  const { mutate: publish, isPending } = useNostrPublish();
  const [content, setContent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  if (!user) return null;

  const submit = () => {
    const text = content.trim();
    if (!text || isPending) return;
    setError(null);
    const tags: string[][] = [];
    if (replyTo) {
      tags.push(['e', replyTo.id, '', 'reply'], ['p', replyTo.pubkey]);
    }
    publish(
      { kind: 1, content: text, tags },
      {
        onSuccess: () => {
          setContent('');
          queryClient.invalidateQueries({ queryKey: ['jumble'] });
          onPublished?.();
        },
        onError: (e) => setError(e.message || 'Publish failed'),
      },
    );
  };

  return (
    <div className="border-b p-4">
      <div className="flex gap-3">
        <UserAvatar pubkey={user.pubkey} />
        <div className="flex-1">
          {replyTo && <p className="mb-1 text-xs text-muted-foreground">Replying…</p>}
          <Textarea
            value={content}
            autoFocus={autoFocus}
            onChange={(e) => setContent(e.target.value)}
            placeholder={placeholder}
            rows={3}
            className="resize-none border-0 bg-transparent p-0 text-lg shadow-none focus-visible:ring-0"
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') submit();
            }}
          />
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="mt-2 flex items-center justify-between">
            <span className="text-xs text-muted-foreground">{content.length}/10000 · ⌘+Enter to post</span>
            <Button size="sm" className="rounded-full px-5" disabled={!content.trim() || isPending} onClick={submit}>
              {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Post
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
